import { AxiosInstance } from 'axios';
import { EventStatus } from 'enum/event-status';
import { Role } from 'enum/roles';
import { ListOrganizationsResponse } from 'services/api';
import { BaseApi } from 'services/api/base-api';
import { ApiResult } from 'services/api/result.type';
import { ListEventsResponse } from '../events/dto';
import { ListTicketsResponse } from '../tickets/dto';
import { FindUserByEmailResponse, ListUsersResponse } from '../users/dto';
import { GetStatsResponse } from './dto/get-stats.response.dto';
import { CreateUserRequest } from './dto';
import { MasterPanelListUsersResponse } from './dto/list-users.response.dto';

export class MasterApi extends BaseApi {
  public constructor (protected readonly api: AxiosInstance) {
    super();

    this.createUser = this.createUser.bind(this);
    this.getUsers = this.getUsers.bind(this);
    this.getStats = this.getStats.bind(this);
    this.findByEmail = this.findByEmail.bind(this);
    this.listSchoolUsers = this.listSchoolUsers.bind(this);
    this.listSchoolEvents = this.listSchoolEvents.bind(this);
    this.listSchoolTickets = this.listSchoolTickets.bind(this);
    this.listSchoolOrganizations = this.listSchoolOrganizations.bind(this);
    this.importEvents = this.importEvents.bind(this);
    this.importOrganizations = this.importOrganizations.bind(this);
  }

  public async createUser (data: CreateUserRequest): ApiResult<void> {
    return await this.post<void>('/master/users', data);
  }

  public async getUsers ({
    page,
    search,
    roles,
  }: {
    page?: number;
    search: string | null;
    roles?: Role[];
  }): ApiResult<MasterPanelListUsersResponse> {
    return await this.get<MasterPanelListUsersResponse>('/master/users', {
      params: { page, search, roles },
    });
  }

  public async getStats (): ApiResult<GetStatsResponse> {
    return await this.get<GetStatsResponse>('/master/stats');
  }

  public async findByEmail (
    schoolId: string,
    email: string,
  ): ApiResult<FindUserByEmailResponse> {
    return await this.get<FindUserByEmailResponse>(
      `/schools/${schoolId}/users/email/${email}`,
    );
  }

  async listSchoolUsers (
    schoolId: string,
    {
      page,
      pageSize,
      search,
      roles,
    }: {
      page?: number;
      pageSize?: number;
      search?: string | null;
      roles?: Role[];
    },
  ): ApiResult<ListUsersResponse> {
    return await this.get<ListUsersResponse>(`/schools/${schoolId}/users`, {
      params: {
        page,
        pageSize,
        search,
        roles,
      },
    });
  }

  public async listSchoolEvents (
    schoolId: string,
    {
      page,
      statuses,
      search,
    }: {
      page: number;
      search: string | null;
      statuses: EventStatus[];
    },
  ): ApiResult<ListEventsResponse> {
    return await this.get<ListEventsResponse>(`/schools/${schoolId}/events`, {
      params: {
        page,
        search,
        statuses,
      },
    });
  }

  async listSchoolTickets (
    schoolId: string,
    {
      page,
      pageSize,
      search,
    }: {
      page?: number;
      pageSize?: number;
      search?: string | null;
    },
  ): ApiResult<ListTicketsResponse> {
    return await this.get<ListTicketsResponse>(`/schools/${schoolId}/tickets`, {
      params: {
        page,
        pageSize,
        search,
      },
    });
  }

  async listSchoolOrganizations (
    schoolId: string,
    {
      page,
      pageSize,
      search,
      cursor,
    }: {
      page?: number;
      pageSize?: number;
      search?: string | null;
      cursor?: string | null;
    },
  ): ApiResult<ListOrganizationsResponse> {
    const params: {
      page?: number;
      pageSize?: number;
      search?: string | null;
      cursor?: string | null;
    } = {
      pageSize,
      search,
    };

    if (cursor) params.cursor = cursor;
    else params.page = page;

    return await this.get<ListOrganizationsResponse>(
      `/schools/${schoolId}/organizations`,
      {
        params: {
          ...params,
          includeInactive: 'true',
        },
      },
    );
  }

  public async importEvents (
    schoolId: string,
    file: File,
    {
      onProgress,
    }: {
      onProgress?: (e: ProgressEvent) => void;
    } = {},
  ): ApiResult<{ url: string }> {
    const formData = new FormData();
    formData.append('schoolId', schoolId);
    formData.append('file', file);

    const result = await this.post<{ url: string }>(
      '/master/importEvents',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onProgress,
      },
    );

    return result;
  }

  public async importOrganizations (
    schoolId: string,
    file: File,
    {
      onProgress,
    }: {
      onProgress?: (e: ProgressEvent) => void;
    } = {},
  ): ApiResult<{ url: string }> {
    const formData = new FormData();
    formData.append('schoolId', schoolId);
    formData.append('file', file);

    const result = await this.post<{ url: string }>(
      '/master/importOrganizations',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onProgress,
      },
    );

    return result;
  }

  public async listMasterMajors (): ApiResult<{
    majors: Array<{ id: string; name: string }>;
  }> {
    return await this.get('/master/majors');
  }

  public async createMasterMajor (
    name: string,
  ): ApiResult<{ id: string; name: string }> {
    return await this.post('/master/majors', { name });
  }

  public async updateMasterMajor (
    id: string,
    name: string,
  ): ApiResult<{ id: string; name: string }> {
    return await this.patch(`/master/majors/${id}`, { name });
  }

  public async deleteMasterMajor (id: string): ApiResult<void> {
    return await this.delete(`/master/majors/${id}`);
  }
}
